import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";

interface CompanyProfile {
  id: string;
  name: string;
  industry: string;
  company_size: string;
  location: string;
  address: string;
  phone: string;
  email: string;
  website: string;
  founded: number;
  description: string;
  benefits: string[];
}

const industryOptions = [
  "Công nghệ thông tin",
  "Sản xuất",
  "Xây dựng",
  "Thương mại - Bán lẻ",
  "Dịch vụ",
  "Vận tải - Logistics",
  "Tài chính - Ngân hàng",
  "Giáo dục",
  "Y tế",
  "Nông nghiệp",
  "Khách sạn - Nhà hàng",
  "Bất động sản",
  "Khác",
];

const sizeOptions = [
  "Dưới 10 nhân viên",
  "10 - 50 nhân viên",
  "50 - 100 nhân viên",
  "100 - 300 nhân viên",
  "300 - 500 nhân viên",
  "500 - 1000 nhân viên",
  "Trên 1000 nhân viên",
];

export default function CompanyProfileTab() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<CompanyProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [successMsg, setSuccessMsg] = useState("");
  const [errorMsg, setErrorMsg] = useState("");

  const [form, setForm] = useState({
    name: "",
    industry: "",
    company_size: "",
    location: "",
    address: "",
    phone: "",
    website: "",
    founded: 0,
    description: "",
  });

  const [benefitInput, setBenefitInput] = useState("");

  const fetchProfile = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      setError(null);
      const { data, error: supabaseError } = await supabase
        .from("vietnamcv_company_profiles")
        .select("*")
        .eq("id", user.id)
        .maybeSingle();

      if (supabaseError) throw supabaseError;

      if (data) {
        const cp = data as Record<string, unknown>;
        const p: CompanyProfile = {
          id: cp.id as string,
          name: (cp.name as string) || "",
          industry: (cp.industry as string) || "",
          company_size: (cp.company_size as string) || "",
          location: (cp.location as string) || "",
          address: (cp.address as string) || "",
          phone: (cp.phone as string) || "",
          email: (cp.email as string) || user.email,
          website: (cp.website as string) || "",
          founded: (cp.founded as number) || 0,
          description: (cp.description as string) || "",
          benefits: Array.isArray(cp.benefits) ? cp.benefits as string[] : [],
        };
        setProfile(p);
        setForm({
          name: p.name,
          industry: p.industry,
          company_size: p.company_size,
          location: p.location || p.address,
          address: p.address || p.location,
          phone: p.phone,
          website: p.website,
          founded: p.founded,
          description: p.description,
        });
      } else {
        // Fallback to profiles data
        const { data: profileData } = await supabase
          .from("vietnamcv_profiles")
          .select("company_name, phone, address")
          .eq("id", user.id)
          .maybeSingle();

        setForm({
          name: profileData?.company_name || user.company_name || "",
          industry: "",
          company_size: "",
          location: "",
          address: profileData?.address || "",
          phone: profileData?.phone || user.phone || "",
          website: "",
          founded: 0,
          description: "",
        });
        setProfile({
          id: user.id,
          name: profileData?.company_name || user.company_name || "",
          industry: "",
          company_size: "",
          location: "",
          address: profileData?.address || "",
          phone: profileData?.phone || user.phone || "",
          email: user.email,
          website: "",
          founded: 0,
          description: "",
          benefits: [],
        });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Không thể tải dữ liệu");
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  const addBenefit = () => {
    const trimmed = benefitInput.trim();
    if (!trimmed) return;
    if (!profile) return;
    if (profile.benefits.includes(trimmed)) return;
    setProfile({ ...profile, benefits: [...profile.benefits, trimmed] });
    setBenefitInput("");
  };

  const removeBenefit = (index: number) => {
    if (!profile) return;
    setProfile({
      ...profile,
      benefits: profile.benefits.filter((_, i) => i !== index),
    });
  };

  const handleSave = async () => {
    if (!user?.id || !profile) return;
    setSaving(true);
    setSuccessMsg("");
    setErrorMsg("");

    try {
      // Update company_profiles
      const { error: cpError } = await supabase
        .from("vietnamcv_company_profiles")
        .upsert({
          id: user.id,
          name: form.name,
          industry: form.industry,
          company_size: form.company_size,
          location: form.location || form.address,
          address: form.address || form.location,
          phone: form.phone,
          email: form.email || user.email,
          website: form.website,
          founded: form.founded || null,
          description: form.description,
          benefits: profile.benefits,
          updated_at: new Date().toISOString(),
        }, { onConflict: "id" });

      if (cpError) throw cpError;

      // Also update profiles table
      const { error: pError } = await supabase
        .from("vietnamcv_profiles")
        .update({
          company_name: form.name,
          phone: form.phone,
          address: form.address || form.location,
          updated_at: new Date().toISOString(),
        })
        .eq("id", user.id);

      if (pError) throw pError;

      setSuccessMsg("Đã lưu hồ sơ doanh nghiệp thành công!");
      setTimeout(() => setSuccessMsg(""), 4000);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Lỗi khi lưu dữ liệu");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-background-50 rounded-xl border border-background-200/70 p-10 space-y-6 animate-pulse">
        <div className="h-6 w-1/3 bg-background-200 rounded" />
        <div className="space-y-4">
          <div className="h-10 bg-background-200 rounded-xl" />
          <div className="h-10 bg-background-200 rounded-xl" />
          <div className="h-10 bg-background-200 rounded-xl" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-background-50 rounded-xl border border-background-200/70 p-10 flex flex-col items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
          <i className="ri-error-warning-line text-xl text-red-500"></i>
        </div>
        <p className="text-sm text-foreground-600">{error}</p>
        <button
          onClick={() => { setLoading(true); fetchProfile(); }}
          className="inline-flex items-center gap-2 px-4 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-refresh-line text-sm"></i>
          <span>Thử lại</span>
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h3 className="font-heading text-lg font-bold text-foreground-950">Hồ sơ doanh nghiệp</h3>
          <p className="text-xs text-foreground-500 mt-0.5">Thông tin này sẽ hiển thị công khai trên trang hồ sơ doanh nghiệp</p>
        </div>
      </div>

      {successMsg && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
          <i className="ri-check-line text-base flex-shrink-0"></i>
          <span>{successMsg}</span>
        </div>
      )}

      {errorMsg && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <i className="ri-error-warning-line text-base flex-shrink-0"></i>
          <span>{errorMsg}</span>
        </div>
      )}

      <div className="bg-background-50 rounded-xl border border-background-200/70 p-6 space-y-6">
        {/* Basic Info */}
        <div>
          <h4 className="font-heading font-semibold text-foreground-900 mb-4 text-sm">Thông tin cơ bản</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">Tên doanh nghiệp</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3.5 py-2.5 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                placeholder="Công ty TNHH ABC"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">Ngành nghề</label>
              <select
                value={form.industry}
                onChange={(e) => setForm({ ...form, industry: e.target.value })}
                className="w-full px-3.5 py-2.5 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer appearance-none"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 12px center",
                  paddingRight: "2.5rem",
                }}
              >
                <option value="">Chọn ngành nghề</option>
                {industryOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">Quy mô</label>
              <select
                value={form.company_size}
                onChange={(e) => setForm({ ...form, company_size: e.target.value })}
                className="w-full px-3.5 py-2.5 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all cursor-pointer appearance-none"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23999' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "right 12px center",
                  paddingRight: "2.5rem",
                }}
              >
                <option value="">Chọn quy mô</option>
                {sizeOptions.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">Năm thành lập</label>
              <input
                type="number"
                value={form.founded || ""}
                onChange={(e) => setForm({ ...form, founded: parseInt(e.target.value) || 0 })}
                className="w-full px-3.5 py-2.5 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                placeholder="VD: 2015"
                min="1900"
                max={new Date().getFullYear()}
              />
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="border-t border-background-100 pt-6">
          <h4 className="font-heading font-semibold text-foreground-900 mb-4 text-sm">Thông tin liên hệ</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">Email</label>
              <input
                type="email"
                value={form.email || user?.email || ""}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="w-full px-3.5 py-2.5 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                placeholder="lienhe@doanhnghiep.com"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">Số điện thoại</label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full px-3.5 py-2.5 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                placeholder="0912345678"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">Website</label>
              <input
                type="url"
                value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })}
                className="w-full px-3.5 py-2.5 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                placeholder="https://www.doanhnghiep.com"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-foreground-700 mb-1.5">Địa điểm</label>
              <input
                type="text"
                value={form.location}
                onChange={(e) => setForm({ ...form, location: e.target.value })}
                className="w-full px-3.5 py-2.5 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
                placeholder="TP. Hồ Chí Minh"
              />
            </div>
          </div>
          <div className="mt-4">
            <label className="block text-xs font-medium text-foreground-700 mb-1.5">Địa chỉ đầy đủ</label>
            <input
              type="text"
              value={form.address}
              onChange={(e) => setForm({ ...form, address: e.target.value })}
              className="w-full px-3.5 py-2.5 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              placeholder="Số 123, Đường ABC, Quận XYZ, TP.HCM"
            />
          </div>
        </div>

        {/* Description */}
        <div className="border-t border-background-100 pt-6">
          <h4 className="font-heading font-semibold text-foreground-900 mb-4 text-sm">Giới thiệu doanh nghiệp</h4>
          <textarea
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={4}
            maxLength={500}
            className="w-full px-3.5 py-2.5 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all resize-none"
            placeholder="Mô tả về doanh nghiệp của bạn, lịch sử hình thành, lĩnh vực hoạt động..."
          />
          <p className="text-xs text-foreground-400 mt-1 text-right">{form.description.length}/500</p>
        </div>

        {/* Benefits */}
        <div className="border-t border-background-100 pt-6">
          <h4 className="font-heading font-semibold text-foreground-900 mb-4 text-sm">Phúc lợi & chế độ đãi ngộ</h4>
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={benefitInput}
              onChange={(e) => setBenefitInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addBenefit(); } }}
              className="flex-1 px-3.5 py-2.5 text-sm bg-background-100 border border-background-200 rounded-xl text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100 transition-all"
              placeholder="VD: Bảo hiểm sức khỏe, Thưởng tháng 13..."
            />
            <button
              type="button"
              onClick={addBenefit}
              className="px-4 py-2.5 bg-secondary-500 text-background-50 rounded-xl text-sm font-semibold hover:bg-secondary-600 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-add-line text-base"></i> Thêm
            </button>
          </div>
          {profile && profile.benefits.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {profile.benefits.map((b, i) => (
                <span
                  key={i}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-secondary-50 border border-secondary-200 rounded-full text-xs text-secondary-700"
                >
                  <i className="ri-check-line text-secondary-500 text-xs"></i>
                  {b}
                  <button
                    onClick={() => removeBenefit(i)}
                    className="ml-0.5 w-4 h-4 flex items-center justify-center rounded-full hover:bg-secondary-200 transition-colors cursor-pointer"
                  >
                    <i className="ri-close-line text-secondary-500 text-xs"></i>
                  </button>
                </span>
              ))}
            </div>
          ) : (
            <p className="text-xs text-foreground-400">Chưa có phúc lợi nào. Thêm phúc lợi để thu hút ứng viên.</p>
          )}
        </div>

        {/* Save Button */}
        <div className="border-t border-background-100 pt-6 flex items-center justify-end gap-3">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <i className="ri-loader-4-line animate-spin text-base"></i>
                <span>Đang lưu...</span>
              </>
            ) : (
              <>
                <i className="ri-save-line text-base"></i>
                <span>Lưu thay đổi</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}